import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DigisubservicesService } from '../services/digisubservices.service';
import { catchError, map } from 'rxjs/operators';
import { forkJoin, of, Observable } from 'rxjs';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-expense-ledger-report',
  templateUrl: './expense-ledger-report.component.html',
  styleUrl: './expense-ledger-report.component.scss'
})
export class ExpenseLedgerReportComponent {
  endDate: any;
  startDate: any;
  mainexp: any;
  subexp: any;
  vouchers: any = [];
  expName: any;
  subExpName: any;
  openingBalType: any;
  openingBalance: number;
  currentDate = new Date();
  finalTotal: any;
  drTotal: string;
  crTotal: string;
  expenseList: any[];
  voucher_date: string;
  isLoading: boolean = true;

  constructor(
    private datePipe: DatePipe,
    private dialogRef: MatDialogRef<ExpenseLedgerReportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private service: DigisubservicesService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.startDate = this.data['startDate'];
    this.endDate = this.data['endDate'];
    this.mainexp = this.data['mainexp'].id;
    this.subexp = this.data['subexp'];

    const year = new Date(this.endDate).getFullYear();
    const voucherDate = new Date(year, 2, 31); // March = 2

    // Format manually to avoid timezone conversion
    const yyyy = voucherDate.getFullYear();
    const mm = String(voucherDate.getMonth() + 1).padStart(2, '0');
    const dd = String(voucherDate.getDate()).padStart(2, '0');

    this.voucher_date = `${yyyy}-${mm}-${dd}`;

    this.getExpenseLedgerReportDataNew();

  }

  getExpenseLedgerReportDataNew() {
    this.isLoading = true;

    // Case 1: startDate = 2024-04-01 → this is the very first period, so the
    // API's own opening balance (normally 0) is used directly.
    if (this.startDate === '2024-04-01') {
      this.buildEnrichedExpenseData(this.startDate, this.endDate).subscribe(expenseRes => {
        this.handleLedgerData(expenseRes);
      });
    } else {
      // Any later period: the opening balance is the closing balance carried
      // forward from everything since inception (2024-04-01) up to the day
      // before this period starts. That earlier data needs the exact same
      // enrichment (billing/personal-ledger merges) as the period being
      // displayed - otherwise the carried-forward balance won't match what
      // this same report showed as the previous period's closing balance,
      // and the merged rows (e.g. SGST billing entries) will silently be
      // missing whenever this isn't the very first year.
      forkJoin({
        previousData: this.buildEnrichedExpenseData('2024-04-01', this.getPreviousDate(this.startDate)),
        data: this.buildEnrichedExpenseData(this.startDate, this.endDate)
      }).subscribe(({ previousData, data }) => {
        const { closingBal, closingType } = this.calculateClosing(previousData);

        if (data.exp_type === 'Income' || data.exp_type === 'Expenditure') {
          data.opening_bal = 0;
        } else {
          data.opening_bal = closingBal;
        }
        data.opening_bal_type = closingType;
        this.handleLedgerData(data);
      });
    }
  }

  /**
   * Fetches the ledger's base data for the given date range, plus - for the
   * mainexp/subexp combinations that need it - the extra personal-ledger /
   * billing data merged in as additional voucher rows. Used both for the
   * period actually being displayed and for the "since inception" period
   * used to compute an opening balance, so both go through identical logic.
   */
  private buildEnrichedExpenseData(startDate: string, endDate: string): Observable<any> {
    const payload = {
      exp_id: this.mainexp,
      sub_exp_id: this.subexp,
      startdate: startDate,
      enddate: endDate
    };

    // Placeholder date (31 March of the fiscal year this endDate falls in)
    // used for rows that don't have their own real transaction date.
    const placeholderDate = this.getFiscalYearEndDate(endDate);

    const apiCalls: any = {
      expenseRes: this.service.getExpenseLedgerReportData(payload).pipe(
        catchError(err => {
          console.error('Error in getExpenseLedgerReportData', err);
          return of({ data: [], vouchers: [] });
        })
      )
    };

    // Add specific API calls based on mainexp value
    switch (this.mainexp) {
      // Advertisement related (130, 93, 98, 60, 73, 74, 75)
      case '130':
      case '93':
      case '98':
      case '60':
      case '73':
      case '74':
      case '75':
        apiCalls.personalRes = this.service.getPersoanlLedBriefly({
          "start_date": startDate,
          "end_date": endDate,
          "advt_name": ''
        }).pipe(catchError(err => {
          console.error('Error in getPersoanlLedBriefly', err);
          return of({ data: [] });
        }));

        if (['60', '73', '74', '75'].includes(this.mainexp)) {
          apiCalls.advtBillingRes = this.service.getAdvtInvoiceRegisterListbriefly({
            "start_date": startDate,
            "end_date": endDate,
            "issue_name": '',
            "issue_date": '',
          }).pipe(catchError(err => {
            console.error('Error in getAdvtInvoiceRegisterListbriefly', err);
            return of({ data: [] });
          }));
        }
        break;

      // Issue Seller related (123, 115, 87)
      case '123':
      case '115':
      case '87':
        apiCalls.issueSellerpersonalRes = this.service.getIssuePersoanlLedBriefly({
          startDate: startDate,
          endDate: endDate,
          issue_seller_name: ''
        }).pipe(catchError(err => {
          console.error('Error in getIssuePersoanlLedBriefly', err);
          return of({ data: [] });
        }));

        if (this.mainexp === '87') {
          apiCalls.issueSellerBillingRes = this.service.getissueInvoiceRegisterListbriefly({
            "start_date": startDate,
            "end_date": endDate,
            "issue_name": '',
            "issue_date": '',
          }).pipe(catchError(err => {
            console.error('Error in getissueInvoiceRegisterListbriefly', err);
            return of({ data: [] });
          }));
        }
        break;

      // Book Seller related (94, 97, 85)
      case '94':
      case '97':
      case '85':
        apiCalls.bookpersonalRes = this.service.getBookSellerLedgerBriefly({
          start_date: startDate,
          end_date: endDate,
          book_seller_name: ''
        }).pipe(catchError(err => {
          console.error('Error in getBookSellerLedgerBriefly', err);
          return of({ data: [] });
        }));

        if (this.mainexp === '85') {
          apiCalls.bookBillingRes = this.service.getdataforbookbilling().pipe(
            catchError(err => {
              console.error('Error in getdataforbookbilling', err);
              return of({ data: [] });
            })
          );
        }
        break;
    }

    switch (this.subexp) {
      case '264':
        if (this.mainexp === '128') {
          apiCalls.openingbookstock = this.service.getBookStockOpeningData({
            book_name: 'All',
            start_date: startDate,
            end_date: endDate
          }).pipe(
            catchError(err => {
              console.error('Error in getBookStockOpeningData', err);
              return of({ data: [] });
            })
          );
        }
        break;
      case '266':
        if (this.mainexp === '129') {
          apiCalls.openingbookstock = this.service.getBookStockOpeningData({
            book_name: 'All',
            start_date: startDate,
            end_date: endDate
          }).pipe(
            catchError(err => {
              console.error('Error in getBookStockOpeningData', err);
              return of({ data: [] });
            })
          );
        }
        break;
    }

    return forkJoin(apiCalls).pipe(
      map((responses: any) => {
        const expenseRes = responses.expenseRes || { vouchers: [] };
        const personalRes = responses.personalRes;
        const issueSellerpersonalRes = responses.issueSellerpersonalRes;
        const bookpersonalRes = responses.bookpersonalRes;
        const advtBillingRes = responses.advtBillingRes;
        const issueSellerBillingRes = responses.issueSellerBillingRes;
        const bookBillingRes = responses.bookBillingRes;
        const openingBookStockRes = responses.openingbookstock;

        // Process based on mainexp
        switch (this.mainexp) {
          case '130':
            // TDS entries from personalRes
            if (Array.isArray(personalRes?.data)) {
              const tdsVouchers = personalRes.data
                .filter((it: any) => parseFloat(it.tds_amt || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: `DUMMY-TDS-${idx + 1}`,
                  voucher_date: placeholderDate,
                  rep_name: it.advt_name,
                  narration: it.narration,
                  exp_amt: String(Math.abs(parseFloat(it.tds_amt || '0'))),
                  voucher_type: 'DR'
                }));
              expenseRes.vouchers.push(...tdsVouchers);
            }
            break;

          case '93':
            // Credit note entries from personalRes
            if (Array.isArray(personalRes?.data)) {
              const creditNoteVouchers = personalRes.data
                .filter((it: any) => parseFloat(it.other_credit_amt || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: `DUMMY-TDS-${idx + 1}`,
                  voucher_date: placeholderDate,
                  rep_name: it.advt_name,
                  narration: it.narration,
                  exp_amt: String(Math.abs(parseFloat(it.other_credit_amt || '0'))),
                  voucher_type: 'DR'
                }));
              expenseRes.vouchers.push(...creditNoteVouchers);
            }
            break;

          case '98':
            // Outstanding entries from personalRes
            if (Array.isArray(personalRes?.data)) {
              const outstandingVouchers = personalRes.data
                .filter((it: any) => parseFloat(it.balance_amt || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: '',
                  voucher_date: placeholderDate,
                  rep_name: it.advt_name,
                  narration: it.narration,
                  exp_amt: String(parseFloat(it.balance_amt || '0')),
                  voucher_type: 'DR'
                }));
              expenseRes.vouchers.push(...outstandingVouchers);
            }
            break;

          case '123':
            // Credit note entries from issueSellerpersonalRes
            if (Array.isArray(issueSellerpersonalRes)) {
              const creditNoteVouchers = issueSellerpersonalRes
                .filter((it: any) => parseFloat(it.credit_amt || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: `DUMMY-TDS-${idx + 1}`,
                  voucher_date: placeholderDate,
                  rep_name: it.issue_seller_name,
                  narration: '',
                  exp_amt: String(Math.abs(parseFloat(it.credit_amt || '0'))),
                  voucher_type: 'DR'
                }));
              expenseRes.vouchers.push(...creditNoteVouchers);
            }
            break;

          case '115':
            // Outstanding entries from issueSellerpersonalRes
            if (Array.isArray(issueSellerpersonalRes)) {
              const outstandingVouchers = issueSellerpersonalRes
                .filter((it: any) => parseFloat(it.bal_amt || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: '',
                  voucher_date: placeholderDate,
                  rep_name: it.issue_seller_name,
                  narration: '',
                  exp_amt: String(parseFloat(it.bal_amt || '0')),
                  voucher_type: 'DR'
                }));
              expenseRes.vouchers.push(...outstandingVouchers);
            }
            break;

          case '94':
            // Credit note entries from bookpersonalRes
            if (Array.isArray(bookpersonalRes)) {
              const creditNoteVouchers = bookpersonalRes
                .filter((it: any) => parseFloat(it.credit_amt || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: '',
                  voucher_date: placeholderDate,
                  rep_name: it.book_seller_name,
                  narration: '',
                  exp_amt: String(Math.abs(parseFloat(it.credit_amt || '0'))),
                  voucher_type: 'DR'
                }));
              expenseRes.vouchers.push(...creditNoteVouchers);
            }
            break;

          case '97':
            // Outstanding entries from bookpersonalRes
            if (Array.isArray(bookpersonalRes)) {
              const outstandingVouchers = bookpersonalRes
                .filter((it: any) => parseFloat(it.bal_amt || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: '',
                  voucher_date: placeholderDate,
                  rep_name: it.book_seller_name,
                  narration: '',
                  exp_amt: String(parseFloat(it.bal_amt || '0')),
                  voucher_type: 'DR'
                }));
              expenseRes.vouchers.push(...outstandingVouchers);
            }
            break;

          case '60':
            // Billing vouchers from advtBillingRes
            if (Array.isArray(advtBillingRes)) {
              const billingVouchers = advtBillingRes
                .filter((it: any) => parseFloat(it.beforegst_amt || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: it.invoice_no,
                  voucher_date: it.invoice_date,
                  rep_name: it.advt_name,
                  narration: '',
                  exp_amt: String(parseFloat(it.beforegst_amt || '0')),
                  voucher_type: 'CR'
                }));
              expenseRes.vouchers.push(...billingVouchers);
            }
            break;

          case '73':
            // CGST vouchers from advtBillingRes
            if (Array.isArray(advtBillingRes)) {
              const billingVouchers = advtBillingRes
                .filter((it: any) => parseFloat(it.cgst || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: it.invoice_no,
                  voucher_date: it.invoice_date,
                  rep_name: it.advt_name,
                  narration: '',
                  exp_amt: String(parseFloat(it.cgst || '0')),
                  voucher_type: 'CR'
                }));
              expenseRes.vouchers.push(...billingVouchers);
            }
            break;

          case '74':
            // SGST vouchers from advtBillingRes
            if (Array.isArray(advtBillingRes)) {
              const billingVouchers = advtBillingRes
                .filter((it: any) => parseFloat(it.sgst || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: it.invoice_no,
                  voucher_date: it.invoice_date,
                  rep_name: it.advt_name,
                  narration: '',
                  exp_amt: String(parseFloat(it.sgst || '0')),
                  voucher_type: 'CR'
                }));
              expenseRes.vouchers.push(...billingVouchers);
            }
            break;

          case '75':
            // IGST vouchers from advtBillingRes
            if (Array.isArray(advtBillingRes)) {
              const billingVouchers = advtBillingRes
                .filter((it: any) => parseFloat(it.igst || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: it.invoice_no,
                  voucher_date: it.invoice_date,
                  rep_name: it.advt_name,
                  narration: '',
                  exp_amt: String(parseFloat(it.igst || '0')),
                  voucher_type: 'CR'
                }));
              expenseRes.vouchers.push(...billingVouchers);
            }
            break;

          case '87':
            // Billing vouchers from issueSellerBillingRes
            if (Array.isArray(issueSellerBillingRes)) {
              const billingVouchers = issueSellerBillingRes
                .filter((it: any) => parseFloat(it.beforegst_amt || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: it.invoice_no,
                  voucher_date: it.invoice_date,
                  rep_name: it.issue_seller_name,
                  narration: '',
                  exp_amt: String(parseFloat(it.beforegst_amt || '0')),
                  voucher_type: 'CR'
                }));
              expenseRes.vouchers.push(...billingVouchers);
            }
            break;

          case '85':
            // Billing vouchers from bookBillingRes with date filtering
            if (Array.isArray(bookBillingRes)) {
              const billingVouchers = bookBillingRes
                .filter((item: any) => {
                  const invoiceDate = new Date(item.invoice_date);
                  const start = new Date(startDate);
                  const end = new Date(endDate);
                  return (
                    parseFloat(item.invoice_total) !== 0 &&
                    invoiceDate >= start &&
                    invoiceDate <= end
                  );
                })
                .map((it: any, idx: number) => ({
                  voucher_no: it.invoice_no,
                  voucher_date: it.invoice_date,
                  rep_name: it.name,
                  narration: '',
                  exp_amt: String(parseFloat(it.invoice_total || '0')),
                  voucher_type: 'CR'
                }));
              expenseRes.vouchers.push(...billingVouchers);
            }
            break;
        }

        switch (this.subexp) {
          case '264':
            // Opening book stock entries
            if (Array.isArray(openingBookStockRes)) {
              const openingBookStock = openingBookStockRes
                .filter((it: any) => parseFloat(it.opening_value_of_book || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: '',
                  voucher_date: placeholderDate,
                  rep_name: `${it.book_name}`,
                  narration: '',
                  exp_amt: String(Math.abs(parseFloat(it.opening_value_of_book || '0'))),
                  voucher_type: 'DR'
                }));
              expenseRes.vouchers.push(...openingBookStock);
            }
            break;

          case '266':
            // Closing book stock entries
            if (Array.isArray(openingBookStockRes)) {
              const closingBookStock = openingBookStockRes
                .filter((it: any) => parseFloat(it.value_of_book || '0') !== 0)
                .map((it: any, idx: number) => ({
                  voucher_no: '',
                  voucher_date: placeholderDate,
                  rep_name: `${it.book_name}`,
                  narration: '',
                  exp_amt: String(Math.abs(parseFloat(it.value_of_book || '0'))),
                  voucher_type: 'CR'
                }));
              expenseRes.vouchers.push(...closingBookStock);
            }
            break;
        }

        return expenseRes;
      })
    );
  }

  /** 31 March of the fiscal year the given date falls in (yyyy-MM-dd), used
   * as a placeholder voucher_date for rows with no real transaction date. */
  private getFiscalYearEndDate(dateStr: string): string {
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const marchEnd = new Date(year, 2, 31); // March = 2
    const yyyy = marchEnd.getFullYear();
    const mm = String(marchEnd.getMonth() + 1).padStart(2, '0');
    const dd = String(marchEnd.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  /**
   * Some source APIs (e.g. advertisement billing) return voucher_date as
   * "dd-MM-yyyy" text instead of an ISO date, which crashes the `date`
   * pipe (NG02100) and breaks Date-based sorting. Normalize to ISO here.
   */
  normalizeVoucherDate(dateStr: any): any {
    if (typeof dateStr !== 'string') {
      return dateStr;
    }
    const ddMmYyyy = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (ddMmYyyy) {
      const [, dd, mm, yyyy] = ddMmYyyy;
      return `${yyyy}-${mm}-${dd}`;
    }
    return dateStr;
  }

  /** Helper: Format (startDate - 1 day) */
  getPreviousDate(dateStr: string): string {
    const d = new Date(dateStr);
    d.setDate(d.getDate() - 1);
    return this.datePipe.transform(d, 'yyyy-MM-dd')!;
  }

  /** Helper: Calculate closing balance from vouchers */
  calculateClosing(data: any): { closingBal: number; closingType: string } {
    let currentTotal = data?.opening_bal_type === 'DR'
      ? Number(data.opening_bal)
      : -Number(data.opening_bal);

    (data?.vouchers || []).forEach(voucher => {
      const amt = Number(voucher.exp_amt || 0);
      if (voucher.voucher_type === 'DR') currentTotal += amt;
      else if (voucher.voucher_type === 'CR') currentTotal -= amt;
    });

    return {
      closingBal: Math.abs(currentTotal),
      closingType: currentTotal >= 0 ? 'DR' : 'CR'
    };
  }

  /** Common handler for final data binding */
  handleLedgerData(data: any) {
    if (Array.isArray(data)) {
      data = data[0] || {};
    }

    this.expName = data?.exp_name || '';
    this.subExpName = data?.sub_exp_name || '';
    this.openingBalType = data?.opening_bal_type || '';
    this.openingBalance = Number(data?.opening_bal || 0);

    this.vouchers = (data?.vouchers || [])
      .map(voucher => ({
        ...voucher,
        voucher_date: this.normalizeVoucherDate(voucher.voucher_date),
        // Keep the raw number here - calculation() sums these to build the
        // running balance, and rounding each one to a string first (as
        // before) rounds every voucher individually before summing, which
        // drifts by a paisa or two over many rows versus summing the raw
        // values and rounding only the final totals for display.
        exp_amt: Number(voucher.exp_amt || 0)
      }))
      .sort((a, b) =>
        new Date(a.voucher_date).getTime() - new Date(b.voucher_date).getTime()
      );

    this.calculation({ ...data, vouchers: this.vouchers });
    this.isLoading = false;
  }

  calculation(data: any) {
    let currentTotal = this.openingBalType === 'DR'
      ? Number(this.openingBalance)
      : -Number(this.openingBalance);

    let drSum = this.openingBalType === 'DR' ? Number(this.openingBalance) : 0;
    let crSum = this.openingBalType === 'CR' ? Number(this.openingBalance) : 0;

    this.vouchers = (data?.vouchers || []).map(voucher => {
      const amt = Number(voucher.exp_amt || 0);

      if (voucher.voucher_type === 'DR') {
        drSum = drSum + amt;
        currentTotal += amt; // DR increases balance
      } else if (voucher.voucher_type === 'CR') {
        crSum = crSum + amt;
        currentTotal -= amt; // CR decreases balance
      }

      // Determine DR or CR label based on sign
      let typeLabel = currentTotal >= 0 ? 'Dr' : 'Cr';
      let absTotal = Math.abs(currentTotal).toFixed(2);

      return {
        ...voucher,
        exp_amt: amt.toFixed(2),
        total: `${absTotal} ${typeLabel}`
      };
    });

    // Cut the DR and CR totals to 2 decimals the same way the trial balance
    // API does (floor for positive, ceil for negative - i.e. toward zero,
    // never rounded up), then take the balance from the cut totals, so this
    // ledger's closing matches the trial balance to the paisa.
    const cut = (v: number) => v < 0 ? Math.ceil(v * 100) / 100 : Math.floor(v * 100) / 100;
    const drCut = cut(drSum);
    const crCut = cut(crSum);
    const closing = drCut - crCut;

    this.drTotal = drCut.toFixed(2);
    this.crTotal = crCut.toFixed(2);

    // Save final total for "बाकी पुढे" - from the cut totals, and the last
    // row's running total shows the same figure.
    this.finalTotal = this.vouchers.length
      ? `${Math.abs(closing).toFixed(2)} ${closing >= 0 ? 'Dr' : 'Cr'}`
      : null;
    if (this.vouchers.length) {
      this.vouchers[this.vouchers.length - 1].total = this.finalTotal;
    }
  }

  getFormattedDate(): string {
    return this.datePipe.transform(this.currentDate, 'dd-MM-yyyy') || '';
  }

  getFinalTotalNumber(finalTotal: string | number): number {
    if (!finalTotal) return 0;

    // Remove 'Dr' or 'Cr' and any spaces
    const numericPart = finalTotal.toString().replace(/(Dr|Cr)/gi, '').trim();

    // Convert to number
    return parseFloat(numericPart) || 0;
  }

  exportExcel(fileName: string): void {
    // helper to format date as dd-MM-yyyy
    const fmt = (d: any) => {
      if (!d) return '';
      try {
        const dt = new Date(d);
        const parts = dt.toLocaleDateString('en-GB').split('/');
        return `${parts[0]}-${parts[1]}-${parts[2]}`;
      } catch (e) {
        return d?.toString() ?? '';
      }
    };

    const rows: Array<{
      'सांकेतांक': any;
      'दिनांक': any;
      'तपशील': any;
      'डेबिट': any;
      'क्रेडिट': any;
      'रक्कम': any;
    }> = [];

    // Header block (left / center / right info as plain rows)
    rows.push({ 'सांकेतांक': `दिनांक : ${this.getFormattedDate ? this.getFormattedDate() : fmt(new Date())}`, 'दिनांक': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });
    rows.push({ 'सांकेतांक': `पृष्ठ क्र : 1`, 'दिनांक': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });
    rows.push({ 'सांकेतांक': '', 'दिनांक': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });

    rows.push({ 'सांकेतांक': 'हिंदुस्थान प्रकाशन संस्था', 'दिनांक': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });
    rows.push({ 'सांकेतांक': 'विवेक साप्ताहिक', 'दिनांक': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });
    rows.push({ 'सांकेतांक': 'खर्च खाते अनूपुस्तिका', 'दिनांक': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });

    const title = this.expName ? this.expName : '';
    const subtitle = this.subExpName ? ` - ${this.subExpName}` : '';
    rows.push({ 'सांकेतांक': `** ${title}${subtitle} **`, 'दिनांक': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });

    rows.push({ 'सांकेतांक': `दिनांक पासून : ${this.startDate ? fmt(this.startDate) : ''}`, 'दिनांक': `दिनांक पर्यंत : ${this.endDate ? fmt(this.endDate) : ''}`, 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });
    rows.push({ 'सांकेतांक': '', 'दिनांक': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });

    // Column headers
    rows.push({ 'सांकेतांक': 'सांकेतांक', 'दिनांक': 'दिनांक', 'तपशील': 'तपशील', 'डेबिट': 'डेबिट', 'क्रेडिट': 'क्रेडिट', 'रक्कम': 'रक्कम' });

    // Opening balance row (मागील बाकी)
    const openingBalType = this.openingBalType;
    const openingBalance = (this.openingBalance !== undefined && this.openingBalance !== null) ? Number(this.openingBalance) : 0;
    const drOpening = openingBalType === 'DR' ? parseFloat(openingBalance.toFixed(2)) : '';
    const crOpening = openingBalType === 'CR' ? parseFloat(openingBalance.toFixed(2)) : '';

    rows.push({
      'सांकेतांक': '',
      'दिनांक': '',
      'तपशील': 'मागील बाकी :',
      'डेबिट': drOpening,
      'क्रेडिट': crOpening,
      'रक्कम': ''
    });

    // Vouchers
    if (Array.isArray(this.vouchers)) {
      this.vouchers.forEach((item: any) => {
        const voucherNo = item['voucher_no'] ?? '';
        const voucherDate = item['voucher_date'] ? fmt(item['voucher_date']) : '';
        // Build detail text similar to template
        const mainName = this.subExpName || this.expName || '';
        const extras: string[] = [];
        if (item['rep_name']) extras.push(`rep_name: ${item['rep_name']}`);
        if (item['narration']) extras.push(`narration: ${item['narration']}`);
        if (item['names']) extras.push(`${item['names']}`);
        const detail = [mainName, extras.length ? `(${extras.join(', ')})` : ''].filter(Boolean).join(' ');

        // exp_amt in your code is stored as string with two decimals (e.g., "123.45")
        const amt = (item['exp_amt'] !== undefined && item['exp_amt'] !== null && item['exp_amt'] !== '') ? Number(item['exp_amt']) : null;

        const debitCell = item['voucher_type'] === 'DR' ? (amt !== null && !isNaN(amt) ? Number(amt.toFixed(2)) : '') : '';
        const creditCell = item['voucher_type'] === 'CR' ? (amt !== null && !isNaN(amt) ? Number(amt.toFixed(2)) : '') : '';

        // === FIX: use helper to extract numeric part from item.total (which is like "123.45 Dr") ===
        const totalNumeric = this.getFinalTotalNumber(item['total']); // returns number or 0
        const totalCell = !isNaN(totalNumeric) ? Number(totalNumeric.toFixed(2)) : 0;

        rows.push({
          'सांकेतांक': voucherNo,
          'दिनांक': voucherDate,
          'तपशील': detail,
          'डेबिट': debitCell,
          'क्रेडिट': creditCell,
          'रक्कम': totalCell
        });
      });
    }

    // Totals row
    const drTotal = (this.drTotal !== undefined && this.drTotal !== null) ? Number(this.drTotal) : 0;
    const crTotal = (this.crTotal !== undefined && this.crTotal !== null) ? Number(this.crTotal) : 0;

    rows.push({
      'सांकेतांक': '',
      'दिनांक': '',
      'तपशील': 'एकूण :-',
      'डेबिट': parseFloat(Number(drTotal).toFixed(2)),
      'क्रेडिट': parseFloat(Number(crTotal).toFixed(2)),
      'रक्कम': ''
    });

    // Final balance / baki pudhe
    const finalTotal = this.finalTotal ?? '';
    const finalDr = (typeof finalTotal === 'string' && finalTotal.endsWith('Dr')) ? finalTotal : '';
    const finalCr = (typeof finalTotal === 'string' && finalTotal.endsWith('Cr')) ? finalTotal : '';

    rows.push({
      'सांकेतांक': '',
      'दिनांक': '',
      'तपशील': 'बाकी पुढे :-',
      'डेबिट': finalDr,
      'क्रेडिट': finalCr,
      'रक्कम': ''
    });

    // Blank line (footer spacing)
    rows.push({ 'सांकेतांक': '', 'दिनांक': '', 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '', 'रक्कम': '' });

    // Create worksheet & workbook
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows, { skipHeader: true });

    // Set column widths for readability
    (ws as any)['!cols'] = [
      { wch: 14 }, // संकेतांक
      { wch: 14 }, // दिनांक
      { wch: 60 }, // तपशील
      { wch: 14 }, // डेबिट
      { wch: 14 }, // क्रेडिट
      { wch: 14 }  // रक्कम
    ];

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ExpenseReport');
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  }

}